import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { connect } from "react-redux";
import { sortArrayByProperty } from "../util/sortArrayByProperty";
import { sortQuotes, SORT_LIST } from "../util/compare_utils";
import { LoansDesktopView } from "./comparison_views/LoansDesktopView";
import { LoansMobileView } from "./comparison_views/LoansMobileView";
import { ComparisonViewDesktop } from "./comparison_views/ComparisonViewDesktop";
import { ComparisonViewMobile } from "./comparison_views/ComparisonViewMobile";
import { AddModal } from "./comparison_views/molecules/AddModal";
import { ViewDetailsModal } from "./comparison_views/molecules/ViewDetailsModal";
import { IrqSettingsModal } from "./modals/IrqSettingsModal";
import { extractQuoteData } from "../util/extractQuoteData";
import { LoanPdfContext } from "../context/LoanPdfContext";
import { ReviewLoanModal } from "./comparison_views/molecules/ReviewLoanModal";

const IrqFullView = ({
  data,
  customFormEdit,
  onFormEdit,
  runQuotesQuery,
  errors,
  quotes,
  content,
  isValid,
}) => {
  const { renderLoanPdf } = useContext(LoanPdfContext);

  const [isGrid, setGrid] = useState(true);
  const [isComparedIds, setComparedId] = useState([]);
  const [sorting, setSorting] = useState(SORT_LIST[0]);
  const [isComparisonView, setIsComparisonView] = useState(false);
  const [comparedQuotes, setComparedQuotes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [detailsViewObject, setDetailsViewObject] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isReviewRequestLoading, setIsReviewRequestLoading] = useState(false);
  const [reviewRequestSent, setReviewRequestSent] = useState(false);
  const [reviewRequestError, setReviewRequestError] = useState(null);

  const allLoansSorted = useMemo(() => sortQuotes(quotes), [quotes]);

  const setDetails = useCallback(
    (id) => {
      const detailObject = extractQuoteData(id, allLoansSorted);
      setDetailsViewObject(detailObject);
      setShowDetailsModal(true);
    },
    [allLoansSorted]
  );

  const sortedLoans = useMemo(() => {
    return sortArrayByProperty(allLoansSorted, sorting).map((block) => ({
      ...block,
      quotes: sortArrayByProperty(block.quotes, sorting),
    }));
  }, [sorting, allLoansSorted]);

  const getComparedQuotes = (sortedLoans, isComparedIds) => {
    const comparedQuotes = [];
    sortedLoans.forEach((block) => {
      block.quotes.forEach((quote) => {
        if (isComparedIds.includes(quote.id)) {
          comparedQuotes.push(quote);
        }
      });
    });
    return comparedQuotes;
  };

  const uploadPdf = async (blob) => {
    const createUploadResponse = await fetch(
      process.env.REACT_APP_USWF_EMAIL_URL + "/upload",
      {
        method: "POST",
        body: JSON.stringify({ name: "test.pdf" }),
        headers: { "Content-Type": "application/json" },
      }
    );

    if (createUploadResponse.status !== 200) {
      throw new Error(await createUploadResponse.text());
    }

    const { signedUrl, uploadedName } = await createUploadResponse.json();

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      body: blob,
    });

    if (uploadResponse.status !== 200) {
      throw new Error(await uploadResponse.text());
    }

    return uploadedName;
  };

  const renderSelectedQuotes = async (selectedQuoteDetails, quotes) => {
    const renderedBlobs = [];

    if (selectedQuoteDetails) {
      renderedBlobs.push(
        await renderLoanPdf({
          details: selectedQuoteDetails,
          downPayment: data.downPayment,
          insurancePaymentMonth: data.insurancePaymentMonth,
          plannedClosingDate: data.plannedClosingDate,
          hasWaivedEscrow: data.hasWaivedEscrow,
          insurance: data.insurance,
          taxes: data.taxes,
          content,
        })
      );
    }

    // NOTE: Keep this for loop, since renderLoanPdf must be called sequentially. Don't attempt to use
    // Promise.all or similar to parallelize the rendering.
    for (const quote of quotes) {
      renderedBlobs.push(
        await renderLoanPdf({
          details: extractQuoteData(quote.id, allLoansSorted),
          downPayment: data.downPayment,
          insurancePaymentMonth: data.insurancePaymentMonth,
          plannedClosingDate: data.plannedClosingDate,
          hasWaivedEscrow: data.hasWaivedEscrow,
          insurance: data.insurance,
          taxes: data.taxes,
          content,
        })
      );
    }

    return renderedBlobs;
  };

  const sendReviewRequest = async (
    fullName,
    phoneNumber,
    email,
    notes,
    uploadedNames
  ) => {
    const emailResponse = await fetch(
      process.env.REACT_APP_USWF_EMAIL_URL + "/email",
      {
        method: "POST",
        body: JSON.stringify({
          fullName,
          phoneNumber,
          email,
          notes,
          uploadedNames,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (emailResponse.status !== 200) {
      throw new Error(await emailResponse.text());
    }
  };

  const handleReviewSubmit = useCallback(
    async ({ fullName, email, phoneNumber, notes }) => {
      setIsReviewRequestLoading(true);
      setReviewRequestSent(false);
      setReviewRequestError(null);

      try {
        const renderedBlobs = await renderSelectedQuotes(
          detailsViewObject,
          comparedQuotes
        );
        const uploadedNames = await Promise.all(renderedBlobs.map(uploadPdf));
        await sendReviewRequest(
          fullName,
          phoneNumber,
          email,
          notes,
          uploadedNames
        );
        setReviewRequestSent(true);
      } catch (err) {
        setReviewRequestError(err);
      } finally {
        setIsReviewRequestLoading(false);
      }
    },
    [
      detailsViewObject,
      comparedQuotes,
      allLoansSorted,
      data.downPayment,
      data.insurancePaymentMonth,
      data.plannedClosingDate,
      data.hasWaivedEscrow,
      data.insurance,
      data.taxes,
      renderLoanPdf,
    ]
  );

  const handleReviewClick = useCallback(() => {
    setIsReviewRequestLoading(false);
    setReviewRequestSent(false);
    setReviewRequestError(null);
    setShowReviewModal(true);
  }, []);

  const handleShareClick = useCallback(async () => {
    if (isSharing) return;
    setIsSharing(true);

    const blob = await renderLoanPdf({
      details: detailsViewObject,
      downPayment: data.downPayment,
      insurancePaymentMonth: data.insurancePaymentMonth,
      plannedClosingDate: data.plannedClosingDate,
      hasWaivedEscrow: data.hasWaivedEscrow,
      insurance: data.insurance,
      taxes: data.taxes,
      content,
    });

    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");

    setIsSharing(false);
  }, [renderLoanPdf, isSharing, detailsViewObject, data]);

  useEffect(() => {
    const comparedQuotes = getComparedQuotes(sortedLoans, isComparedIds);
    setComparedQuotes(comparedQuotes);
  }, [isComparedIds, sortedLoans]);

  return (
    <>
      <ReviewLoanModal
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onSubmit={handleReviewSubmit}
        isLoading={isReviewRequestLoading}
        isSuccessfullySent={reviewRequestSent}
        error={reviewRequestError}
      />

      {isComparisonView && comparedQuotes.length >= 1 ? (
        <>
          <ComparisonViewDesktop
            comparedQuotes={comparedQuotes}
            setIsComparisonView={setIsComparisonView}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            enteredData={data}
            setShowSettingsModal={setShowSettingsModal}
            setComparedId={setComparedId}
            isComparedIds={isComparedIds}
            onEditClick={() => {
              setShowAddModal(true);
            }}
            onSelect={(loan) => {
              setDetails(loan.id);
            }}
            content={content}
          />
          <ComparisonViewMobile
            comparedQuotes={comparedQuotes}
            setIsComparisonView={setIsComparisonView}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            enteredData={data}
            setShowSettingsModal={setShowSettingsModal}
            setComparedId={setComparedId}
            isComparedIds={isComparedIds}
            onSelect={(loan) => {
              setDetails(loan.id);
            }}
            onEditClick={() => {
              setShowAddModal(true);
            }}
            content={content}
          />
        </>
      ) : (
        <>
          <LoansDesktopView
            isGrid={isGrid}
            setGrid={setGrid}
            isComparedIds={isComparedIds}
            setComparedId={setComparedId}
            sorting={sorting}
            setSorting={setSorting}
            sortedLoans={sortedLoans}
            setIsComparisonView={setIsComparisonView}
            setDetails={setDetails}
            setShowSettingsModal={setShowSettingsModal}
            comparedQuotes={comparedQuotes}
            showShareModal={showShareModal}
            setShowShareModal={setShowShareModal}
            onFormEdit={onFormEdit}
            enteredData={data}
            content={content}
          />
          <LoansMobileView
            isGrid={isGrid}
            setGrid={setGrid}
            isComparedIds={isComparedIds}
            setComparedId={setComparedId}
            sorting={sorting}
            setSorting={setSorting}
            sortedLoans={sortedLoans}
            setIsComparisonView={setIsComparisonView}
            comparedQuotes={comparedQuotes}
            setDetails={setDetails}
            setShowSettingsModal={setShowSettingsModal}
            enteredData={data}
          />
        </>
      )}
      <AddModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        customFormEdit={customFormEdit}
        enteredData={data}
        onFormEdit={onFormEdit}
        content={content}
      />
      <IrqSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        errors={errors}
        customFormEdit={customFormEdit}
        onFormEdit={onFormEdit}
        isLoading={false}
        enteredData={data}
        runQuotesQuery={runQuotesQuery}
        isValid={isValid}
      />
      <ViewDetailsModal
        taxes={data.taxes}
        insurance={data.insurance}
        downPayment={data.downPayment}
        insurancePaymentMonth={data.insurancePaymentMonth}
        plannedClosingDate={data.plannedClosingDate}
        hasWaivedEscrow={data.hasWaivedEscrow}
        earnestMoney={data.earnestMoney}
        credits={data.credits}
        setShowDetailsModal={setShowDetailsModal}
        details={detailsViewObject}
        open={showDetailsModal && !showAddModal}
        onReviewClick={handleReviewClick}
        onShareClick={handleShareClick}
        onEditClick={() => {
          setShowAddModal(true);
        }}
        content={content}
      />
    </>
  );
};

export const PURCHASEIRQCompareView = connect()(IrqFullView);
